import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType } from '../types';
import ApiService from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [originalToken, setOriginalToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = async () => {
    try {
      await ApiService.logout();
    } catch (error) {
      console.error('Errore logout:', error);
    } finally {
      // Rimuovi i dati dal localStorage usando le chiavi specifiche per ambiente
      const tokenKey = ApiService.getUserKey().replace('_user', '_token');
      const userKey = ApiService.getUserKey();
      
      localStorage.removeItem(tokenKey);
      localStorage.removeItem(userKey);
      localStorage.removeItem(`${tokenKey}_last_verified`);
      
      // Rimuovi anche i dati di impersonificazione se presenti
      localStorage.removeItem(userKey + '_orig');
      localStorage.removeItem(tokenKey + '_orig');
      
      // Resetta lo stato
      setToken(null);
      setUser(null);
      setOriginalUser(null);
      setOriginalToken(null);
      
      // Rimuovi il token dall'API service
      ApiService.removeAuthToken();
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const tokenKey = ApiService.getUserKey().replace('_user', '_token');
        const userKey = ApiService.getUserKey();
        
        const savedToken = localStorage.getItem(tokenKey);
        const savedUser = localStorage.getItem(userKey);
        
        const savedOriginalToken = localStorage.getItem(tokenKey + '_orig');
        const savedOriginalUser = localStorage.getItem(userKey + '_orig');
        
        if (savedOriginalToken && savedOriginalUser) {
          setOriginalToken(savedOriginalToken);
          setOriginalUser(JSON.parse(savedOriginalUser));
        }
        
        if (savedToken && savedUser) {
          setToken(savedToken);
          setUser(JSON.parse(savedUser));
          
          // Verifica la scadenza del token solo se necessario
          const lastVerified = localStorage.getItem(`${tokenKey}_last_verified`);
          const now = Date.now();
          
          // Verifica il token solo se non è stato verificato negli ultimi 30 minuti
          if (!lastVerified || now - parseInt(lastVerified) > 30 * 60 * 1000) {
            try {
              console.log('Verifica token necessaria');
              const response = await ApiService.verifyToken();
              setUser(response.user);
              localStorage.setItem(`${tokenKey}_last_verified`, now.toString());
            } catch (error) {
              console.error('Token non valido:', error);
              logout();
            }
          } else {
            console.log('Token già verificato di recente, saltata la verifica');
          }
        }
      } catch (error) {
        console.error('Errore inizializzazione auth:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await ApiService.login({ email, password });
      
      setToken(response.token);
      setUser(response.user);
      
      // Salva in localStorage usando le chiavi specifiche per ambiente
      const tokenKey = ApiService.getUserKey().replace('_user', '_token');
      const userKey = ApiService.getUserKey();
      
      localStorage.setItem(tokenKey, response.token);
      localStorage.setItem(userKey, JSON.stringify(response.user));
      localStorage.setItem(`${tokenKey}_last_verified`, Date.now().toString());
      
      ApiService.setAuthToken(response.token);
      
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithQr = async (tokenStr: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await ApiService.loginWithQr(tokenStr);
      
      setToken(response.token);
      setUser(response.user);
      
      // Salva in localStorage usando le chiavi specifiche per ambiente
      const tokenKey = ApiService.getUserKey().replace('_user', '_token');
      const userKey = ApiService.getUserKey();
      
      localStorage.setItem(tokenKey, response.token);
      localStorage.setItem(userKey, JSON.stringify(response.user));
      localStorage.setItem(`${tokenKey}_last_verified`, Date.now().toString());
      
      ApiService.setAuthToken(response.token);
      
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const impersonateUser = async (userId: number): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await ApiService.impersonate(userId);
      
      const tokenKey = ApiService.getUserKey().replace('_user', '_token');
      const userKey = ApiService.getUserKey();
      
      // Se non siamo già in modalità impersonation, salviamo l'utente originale
      if (!originalUser) {
        const currentToken = token || localStorage.getItem(tokenKey);
        const currentUser = user || (localStorage.getItem(userKey) ? JSON.parse(localStorage.getItem(userKey)!) : null);
        
        if (currentUser && currentToken) {
          setOriginalUser(currentUser);
          setOriginalToken(currentToken);
          localStorage.setItem(userKey + '_orig', JSON.stringify(currentUser));
          localStorage.setItem(tokenKey + '_orig', currentToken);
        }
      }
      
      // Imposta il nuovo utente e token
      setToken(response.token);
      setUser(response.user);
      
      localStorage.setItem(tokenKey, response.token);
      localStorage.setItem(userKey, JSON.stringify(response.user));
      localStorage.setItem(`${tokenKey}_last_verified`, Date.now().toString());
      
      ApiService.setAuthToken(response.token);
      
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const stopImpersonating = () => {
    if (!originalUser || !originalToken) return;
    
    setIsLoading(true);
    
    const tokenKey = ApiService.getUserKey().replace('_user', '_token');
    const userKey = ApiService.getUserKey();
    
    // Ripristina l'utente originale
    setToken(originalToken);
    setUser(originalUser);
    
    localStorage.setItem(tokenKey, originalToken);
    localStorage.setItem(userKey, JSON.stringify(originalUser));
    localStorage.setItem(`${tokenKey}_last_verified`, Date.now().toString());
    
    // Rimuovi backup
    localStorage.removeItem(userKey + '_orig');
    localStorage.removeItem(tokenKey + '_orig');
    
    setOriginalUser(null);
    setOriginalToken(null);
    
    ApiService.setAuthToken(originalToken);
    setIsLoading(false);
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    loginWithQr,
    logout,
    isAuthenticated: !!token && !!user,
    isLoading,
    isImpersonating: !!originalUser,
    originalUser,
    impersonateUser,
    stopImpersonating,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
